// SmartCreateAllChores — Feb 2026 (Randy pt89b). "Smart Create Chores
// Schedule" button (renamed from "Smart Create All Chores"; color matches
// the Capture Pad's Smart Create button so the two feel like a family).
// Sits at the BOTTOM of HouseholdPanel, below Quiet hours.
//
// Below the plan textarea are THREE peer buttons:
//   • Left: "Create 'Send to Silo' Master File" — opens MasterFileModal
//     (huge editable textarea → Push to silo via DocSaveDialog).
//   • Center: "Wipe All Chores from Calendar" — destructive, with confirm.
//   • Right: "Push to capture pad" — bridges plan_text into StagingTab.
//
// Feb 22, 2026 (Randy pt89c-iii):
//   • **Recall** button — lists every completed plan from the last 24h
//     (TTL-enforced on smart_chore_plan_jobs); click one to reload its
//     text into the textarea.
//   • **Local persistence** — the textarea's contents are saved to
//     localStorage keyed by silo, so a browser refresh or accidental
//     logout doesn't lose your generated plan.
import { useState, useEffect } from "react";
import { Sparkle, PaperPlaneRight, Spinner, MagicWand, FileText, Broom, Warning, ClockCounterClockwise, CaretRight, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import MasterFileModal from "@/components/MasterFileModal";
import { useCollapsePref } from "@/lib/uiPrefs";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

const LS_KEY = (siloId) => `mysilo:smart-chore-plan-text:${siloId}`;

export default function SmartCreateAllChores({ siloId, siloName, siloKind }) {
  // Jun 2026 (Randy) — Theme QA dynamic-word sweep.
  const tSmartCreate  = useThemeLabel(T.THEME_KEY_CALENDAR_SMART_CREATE_TITLE);
  const tPanelHeading = useThemeLabel(T.THEME_KEY_SMARTCHORES_HEADING);
  const tPanelIntro   = useThemeLabel(T.THEME_KEY_SMARTCHORES_INTRO);
  const tCapture      = useThemeLabel(T.THEME_KEY_MD_CAPTURE_PULL);
  const [startDate, setStartDate] = useState(todayLocalISO());
  const [busy, setBusy] = useState(false);
  const [planText, setPlanText] = useState("");
  const [pushed, setPushed] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState(false);
  const [wipeBusy, setWipeBusy] = useState(false);
  const [masterOpen, setMasterOpen] = useState(false);
  // Recall popover state (pt89c-iii).
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallBusy, setRecallBusy] = useState(false);
  const [recentPlans, setRecentPlans] = useState([]);
  // Jun 2026 (Randy) — collapse memory for the whole plan-creator panel.
  // Server-backed via useCollapsePref; defaults COLLAPSED (tucked away).
  const [creatorCollapsed, toggleCreator] = useCollapsePref(siloId, "chore_plan_creator", true);

  // Persistence — restore any saved plan text on mount, save on change.
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(LS_KEY(siloId));
      if (saved) setPlanText(saved);
    } catch { /* localStorage unavailable */ }
  }, [siloId]);
  useEffect(() => {
    try {
      if (planText) window.localStorage.setItem(LS_KEY(siloId), planText);
      else window.localStorage.removeItem(LS_KEY(siloId));
    } catch { /* ignore */ }
  }, [siloId, planText]);

  const openRecall = async () => {
    setRecallOpen(true); setRecallBusy(true);
    try {
      const r = await api.get(`/calendar/${siloId}/smart-chore-plan/recent`);
      setRecentPlans(r.data?.plans || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load recent plans.");
      setRecentPlans([]);
    } finally { setRecallBusy(false); }
  };

  const recallPlan = (p) => {
    setPlanText(p.plan_text || ""); setPushed(false); setRecallOpen(false);
    toast.success("Plan recalled — review and push to capture pad.");
  };

  const generate = async () => {
    if (!startDate) { toast.error("Pick a start date."); return; }
    setBusy(true); setPushed(false);
    try {
      // Feb 2026 (Randy pt89c) — this endpoint now returns a job_id
      // immediately and does the LLM work in a background task, then we
      // poll /smart-chore-plan/job/{id} every 2s until status = "done" |
      // "error". Fixes the Cloudflare 502 that killed the synchronous
      // response when the LLM took >30s (usage log showed the full 3000+
      // char output arriving on the backend, but the frontend never saw
      // it, so the textarea stayed empty).
      const r = await api.post(`/calendar/${siloId}/smart-chore-plan`, {
        start_date: startDate,
        tz_offset_minutes: new Date().getTimezoneOffset(),
      });
      const jobId = r.data?.job_id;
      if (!jobId) {
        // Older synchronous shape (kept for safety during rollout).
        const txt = (r.data?.plan_text || "").trim();
        if (!txt) toast.error("The AI returned an empty plan — check that you have chores + at least one person.");
        else { setPlanText(txt); toast.success("Chore schedule ready — review the text, then push to capture pad."); }
        return;
      }
      // Poll every 2s, up to 90s total.
      let txt = "";
      let done = false;
      let errMsg = null;
      for (let i = 0; i < 45; i += 1) {
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const pr = await api.get(`/calendar/smart-chore-plan/job/${jobId}`);
          const st = pr.data?.status;
          if (st === "done") { txt = (pr.data?.plan_text || "").trim(); done = true; break; }
          if (st === "error") { errMsg = pr.data?.error || "Plan generation failed."; done = true; break; }
        } catch { /* transient — keep polling */ }
      }
      if (!done) { toast.error("The plan is taking longer than expected — try again in a moment."); return; }
      if (errMsg) { toast.error(errMsg); return; }
      if (!txt) toast.error("The AI returned an empty plan — check that you have chores + at least one person.");
      else { setPlanText(txt); toast.success("Chore schedule ready — review the text, then push to capture pad."); }
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || "Couldn't generate the plan.");
    } finally { setBusy(false); }
  };

  const pushToPad = () => {
    const txt = (planText || "").trim();
    if (!txt) { toast.error("Nothing to push yet — generate a plan first."); return; }
    window.dispatchEvent(new CustomEvent("mysilo:capture-pad-fill", { detail: { text: txt, source: "smart-chore-plan" } }));
    setPushed(true);
    toast.success("Chore schedule sent to the Capture pad — tap Smart create up top to drop it on the calendar.");
  };

  // Feb 22, 2026 (Randy pt89c-viii) → Feb 24, 2026 (Randy pt89c-xxi).
  // Semantics INVERTED per Randy's feedback: default is now AGGRESSIVE
  // ("delete every item of the selected kinds in the date range, including
  // manually-made items"). The optional toggle below flips it to SAFE mode
  // ("keep any items I manually made"). Same DB flag `match_library_only`
  // — when true it protects manual items; when false it wipes everything.
  const [wipeKinds, setWipeKinds] = useState({ event: true, task: true, routine: true, ping: false });
  const [wipeLibraryOnly, setWipeLibraryOnly] = useState(false);
  // Feb 22, 2026 (Randy pt89c-xiii) — optional date range so a user can
  // scope the clear to just a week/month instead of "all chores ever".
  const [wipeDateFrom, setWipeDateFrom] = useState("");
  const [wipeDateTo, setWipeDateTo] = useState("");
  const toggleKind = (k) => setWipeKinds((cur) => ({ ...cur, [k]: !cur[k] }));
  // Feb 22, 2026 (Randy Sprint) — Request 3 fix. Toasts are OFF on mobile
  // (Rule 1), so the old "Nothing matched" toast made the button feel dead
  // on first click. Now we (a) compute selected kinds INLINE (bulletproof
  // against any stale closure) and (b) surface the result INSIDE the modal
  // as a persistent status card so the outcome is unmistakable.
  const [wipeResult, setWipeResult] = useState(null);

  const wipe = async () => {
    // INLINE read — never trust a closure snapshot for the click handler.
    const kinds = Object.entries(wipeKinds).filter(([, v]) => !!v).map(([k]) => k);
    if (kinds.length === 0) { setWipeResult({ kind: "err", msg: "Pick at least one kind to clear." }); return; }
    if (wipeDateFrom && wipeDateTo && wipeDateFrom > wipeDateTo) {
      setWipeResult({ kind: "err", msg: "End date must be on/after start date." }); return;
    }
    setWipeBusy(true); setWipeResult(null);
    try {
      const r = await api.post(`/calendar/${siloId}/chores/wipe-from-calendar`, {
        kinds, match_library_only: wipeLibraryOnly,
        date_from: wipeDateFrom || null, date_to: wipeDateTo || null,
      });
      const n = r.data?.deleted ?? 0;
      const skippedG = r.data?.skipped_google ?? 0;
      const skippedR = r.data?.skipped_out_of_range ?? 0;
      const notes = [];
      if (skippedG > 0) notes.push(`${skippedG} Google-synced left alone`);
      if (skippedR > 0) notes.push(`${skippedR} outside the date range`);
      const skipNote = notes.length ? ` · ${notes.join(" · ")}` : "";
      if (n === 0) {
        // Zero-matched with library-only ON is the #1 confusion — hint at it.
        const hint = wipeLibraryOnly
          ? " Tip: turn off \"Match my household chore names only\" below to also delete items whose title doesn't match a library chore."
          : "";
        setWipeResult({ kind: "info", msg: `Nothing matched — the calendar is already clear.${skipNote}${hint}` });
      } else {
        setWipeResult({ kind: "ok", msg: `Cleared ${n} item${n === 1 ? "" : "s"} from your calendar.${skipNote}` });
        window.dispatchEvent(new CustomEvent("mysilo:calendar-refresh"));
      }
    } catch (e) {
      setWipeResult({ kind: "err", msg: e.response?.data?.detail || "Couldn't clear." });
    } finally { setWipeBusy(false); }
  };

  return (
    <div className="border hairline rounded-md p-3 bg-[#FBF7EE]" data-testid="smart-chores-panel">
      <button type="button" onClick={toggleCreator} data-testid="chore-plan-creator-chevron"
        aria-expanded={!creatorCollapsed}
        title={creatorCollapsed ? "Show the plan creator" : "Hide the plan creator"}
        className="w-full flex items-center gap-1.5 text-left mb-1.5 hover:opacity-80 transition-opacity">
        {creatorCollapsed
          ? <CaretRight size={14} weight="bold" className="text-[#6B6A68]" />
          : <CaretDown size={14} weight="bold" className="text-[#6B6A68]" />}
        <MagicWand size={14} weight="duotone" className="text-[#C19A4A]" />
        <span className="text-sm font-bold text-[#1C1B1A]">{tPanelHeading}</span>
      </button>
      {!creatorCollapsed && (<>
      <p className="text-[11px] text-[#6B6A68] mb-2 leading-relaxed">
        {tPanelIntro}
      </p>

      {/* Row: Smart Create Chores Schedule + start date */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={generate} disabled={busy} data-testid="smart-chores-generate"
          className="text-base px-5 py-3 rounded-md border-2 border-[#C19A4A] text-[#8A6A24] bg-[#F8F1E2] hover:bg-[#F3E7CB] disabled:opacity-60 flex items-center gap-2 shadow-sm font-medium">
          {busy ? <Spinner size={17} className="animate-spin" /> : <Sparkle size={17} weight="duotone" />}
          {tSmartCreate}
        </button>
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">
          Start date
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            data-testid="smart-chores-start-date"
            className="px-2 py-1.5 border hairline rounded text-xs bg-white" />
          <span className="text-[10px] text-[#B5B3AE] hidden sm:inline">runs through end of month</span>
        </label>
        {/* Feb 22, 2026 (Randy pt89c-iii) — Recall button. Sits on the LEFT
            side of the flex-1 spacer, opposite the Clear button. Opens a
            popover listing every plan generated in this silo within the
            last 24h (TTL-enforced on smart_chore_plan_jobs). Click any
            entry to reload its text into the textarea. */}
        <div className="relative">
          <button
            onClick={openRecall}
            data-testid="smart-chores-recall"
            title="Recall a recently generated plan (last 24h)"
            className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border hairline text-[#4A5D4E] hover:border-[#4A5D4E] hover:bg-[#EAF0EB] flex items-center gap-1"
          >
            <ClockCounterClockwise size={11} weight="duotone" /> Recall
          </button>
          {recallOpen && (
            <>
              <div className="fixed inset-0 z-[190]" onClick={() => setRecallOpen(false)} />
              <div className="absolute z-[200] left-0 top-full mt-1 w-80 max-w-[92vw] bg-white border hairline rounded-lg shadow-xl p-2" data-testid="smart-chores-recall-popover">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] px-2 py-1.5 flex items-center justify-between">
                  <span>Recent plans · last 24h</span>
                  <button onClick={() => setRecallOpen(false)} className="text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="smart-chores-recall-close">×</button>
                </div>
                {recallBusy ? (
                  <div className="px-2 py-3 text-xs text-[#6B6A68] flex items-center gap-1.5">
                    <Spinner size={12} className="animate-spin" /> Loading…
                  </div>
                ) : recentPlans.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-[#6B6A68] italic">No plans yet — the ones you generate here will show up for a day.</div>
                ) : (
                  <ul className="max-h-72 overflow-y-auto">
                    {recentPlans.map((p) => {
                      const d = p.completed_at || p.created_at || "";
                      const dt = d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
                      return (
                        <li key={p.id}>
                          <button
                            onClick={() => recallPlan(p)}
                            data-testid={`smart-chores-recall-item-${p.id}`}
                            className="w-full text-left px-2 py-2 rounded hover:bg-[#F0F1EE] border-b hairline last:border-b-0"
                          >
                            <div className="text-[11px] font-medium text-[#1C1B1A] flex items-center justify-between gap-2">
                              <span>{p.start_date || ""}{p.end_date ? ` → ${p.end_date}` : ""}</span>
                              <span className="text-[10px] text-[#6B6A68] font-normal">{dt}</span>
                            </div>
                            <div className="text-[10px] text-[#6B6A68] mt-0.5 line-clamp-2 leading-snug">{p.preview || "(empty)"}</div>
                            <div className="text-[9px] text-[#B5B3AE] mt-0.5">{(p.chars || 0).toLocaleString()} chars</div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
        <span className="flex-1" />
        {/* Feb 2026 (Randy) — Clear button for the AI plan textarea. Sits at
            the far-right of the "Runs through end of month" row. Small,
            subtle, only visible when there's text to clear. */}
        <button
          onClick={() => { setPlanText(""); setPushed(false); }}
          disabled={!planText}
          data-testid="smart-chores-clear"
          title="Clear the plan textarea"
          className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-40 disabled:hover:text-[#6B6A68] disabled:hover:border-black/10">
          Clear
        </button>
      </div>

      <textarea value={planText} onChange={(e) => { setPlanText(e.target.value); setPushed(false); }}
        placeholder={`After you tap ${tSmartCreate}, the AI plan appears here — editable. Push it to the ${tCapture} pad when it looks right.`}
        rows={8} data-testid="smart-chores-textarea"
        className="mt-3 w-full px-3 py-2 border hairline rounded-md text-sm bg-white focus:outline-none focus:border-[#4A5D4E] resize-y font-mono leading-relaxed" />

      {/* Row: 3 peer buttons — Master File · Wipe All · Push to capture pad */}
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        {/* Left: Master File */}
        <div className="relative">
          <button onClick={() => setMasterOpen(true)} data-testid="master-file-open"
            className="text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
            <FileText size={13} weight="duotone" /> Create &quot;Send to Silo&quot; Master File
          </button>
          <div className="absolute -top-1.5 -right-1.5 z-10">
            <InfoTip icon="eye" iconSize={13} placement="bottom-right" width={340} mobileCenter
              testid="master-file-eye" label="What's the Master File for?">
              Press this to create your Master overview file of your chores — specifically designed for your Tier 1 or Tier 2 Silo memory. Chores, chore descriptions, chore details, Members, chore exemptions, schedules — all in one document, formatted <strong>not for the calendar but for the Silo itself</strong>.{"\n\n"}
              The AI Assistant will use this to answer questions like &quot;who does the dishes on Wednesdays?&quot; without re-parsing every calendar event.
            </InfoTip>
          </div>
        </div>

        {/* Center: Smart Clear — checkbox-driven kind picker */}
        <button onClick={() => setWipeConfirm(true)} data-testid="wipe-chores-btn"
          title="Clear items from your calendar — you'll pick which kinds (events, tasks, routines, pings)"
          className="text-sm px-4 py-2.5 rounded-md border-2 border-[#C16E5A] text-[#A55A48] bg-white hover:bg-[#FBEBE4] flex items-center gap-1.5 shadow-sm font-medium">
          <Broom size={14} weight="duotone" /> Clear from Calendar…
        </button>

        {/* Right: Push to capture pad */}
        <div className="flex items-center gap-2">
          {pushed && <span className="text-[10px] text-[#4A5D4E]">Sent to {tCapture} pad ↑</span>}
          <button onClick={pushToPad} disabled={!planText.trim()} data-testid="smart-chores-push"
            className="text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            <PaperPlaneRight size={13} weight="duotone" /> Push to {tCapture} pad
          </button>
        </div>
      </div>

      {wipeConfirm && (
        <div className="fixed inset-0 z-[210] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !wipeBusy && (setWipeConfirm(false), setWipeResult(null))}>
          <div className="modal-safe bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="wipe-chores-confirm">
            <h3 className="font-serif-display text-lg flex items-center gap-2 mb-2"><Warning size={18} weight="duotone" className="text-[#C16E5A]" /> Clear from the calendar</h3>
            <p className="text-xs text-[#6B6A68] mb-3 leading-relaxed">
              Pick which kinds of items to delete in the selected date range. Your <strong>Chores library</strong> (the recipes) is never touched.{"\n"}
              <span className="text-[10px] text-[#4A5D4E]">Google-synced calendar items are always skipped — they belong to your connected calendar.</span>
            </p>
            <div className="space-y-1.5 mb-4" data-testid="wipe-kinds-picker">
              {[
                { k: "event", label: "All Events", hint: "All one-off appointments with a date (respects the date range above)" },
                { k: "task", label: "All Tasks", hint: "Removes all to-dos with an optional date. Including manual creations. (respects the date range above)" },
                { k: "routine", label: "All Routines", hint: "All recurring habit blocks (weekly). (respects the date range above)" },
                { k: "ping", label: "All Pings", hint: "All recurring notification questions. (respects the date range above)" },
              ].map(({ k, label, hint }) => (
                <label key={k} className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-[#F8F7F3]" data-testid={`wipe-kind-${k}`}>
                  <input type="checkbox" checked={!!wipeKinds[k]} onChange={() => toggleKind(k)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#1C1B1A] font-medium">{label}</div>
                    <div className="text-[10px] text-[#6B6A68]">{hint}</div>
                  </div>
                </label>
              ))}
            </div>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-[#F8F7F3] border-t hairline" data-testid="wipe-library-toggle">
              <input type="checkbox" checked={wipeLibraryOnly} onChange={() => setWipeLibraryOnly((v) => !v)} className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#1C1B1A] font-medium">Keep any items I manually made</div>
                <div className="text-[10px] text-[#6B6A68] leading-relaxed">
                  {wipeLibraryOnly
                    ? "Safe mode. Only deletes items whose title matches a chore in your library — anything you typed by hand stays put."
                    : "Default (destructive). Deletes EVERY item of the selected kinds in the date range, including anything you made manually. Check this box to protect your manual entries."}
                </div>
              </div>
            </label>
            {/* Feb 22, 2026 (Randy pt89c-xiii) — optional date range to
                scope the clear (e.g., "just this week" or "just August"). */}
            <div className="border-t hairline pt-3 mt-1" data-testid="wipe-date-range">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1.5">
                Date range (optional — leave blank for all dates)
              </div>
              <div className="flex items-center gap-2">
                <label className="flex-1 flex flex-col gap-0.5">
                  <span className="text-[10px] text-[#6B6A68]">From</span>
                  <input type="date" value={wipeDateFrom} onChange={(e) => setWipeDateFrom(e.target.value)}
                    data-testid="wipe-date-from"
                    className="px-2 py-1.5 border hairline rounded text-xs bg-white" />
                </label>
                <label className="flex-1 flex flex-col gap-0.5">
                  <span className="text-[10px] text-[#6B6A68]">To</span>
                  <input type="date" value={wipeDateTo} onChange={(e) => setWipeDateTo(e.target.value)}
                    data-testid="wipe-date-to"
                    className="px-2 py-1.5 border hairline rounded text-xs bg-white" />
                </label>
                {(wipeDateFrom || wipeDateTo) && (
                  <button
                    onClick={() => { setWipeDateFrom(""); setWipeDateTo(""); }}
                    data-testid="wipe-date-clear"
                    title="Clear the date range"
                    className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] hover:text-[#1C1B1A] self-end pb-1.5"
                  >
                    Clear
                  </button>
                )}
              </div>
              {(wipeDateFrom || wipeDateTo) && (
                <div className="text-[10px] text-[#6B6A68] mt-1 italic">
                  Items without a specific date (like unscheduled tasks) will be skipped.
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => { setWipeConfirm(false); setWipeResult(null); }} disabled={wipeBusy} className="text-xs px-3 py-2 rounded text-[#6B6A68]" data-testid="wipe-chores-cancel">
                {wipeResult?.kind === "ok" ? "Done" : "Cancel"}
              </button>
              <button onClick={wipe} disabled={wipeBusy || Object.values(wipeKinds).every((v) => !v)} data-testid="wipe-chores-confirm-btn"
                className="text-xs px-4 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A55A48] disabled:opacity-50 flex items-center gap-1.5">
                {wipeBusy && <Spinner size={12} className="animate-spin" />} Clear selected
              </button>
            </div>
            {wipeResult && (
              <div
                data-testid="wipe-chores-result"
                className={`mt-3 text-xs rounded-md border p-2.5 leading-relaxed ${
                  wipeResult.kind === "ok" ? "border-[#4A5D4E] bg-[#EAF0EB] text-[#2E3F32]"
                  : wipeResult.kind === "err" ? "border-[#C16E5A] bg-[#FBEBE4] text-[#8A3A28]"
                  : "border-[#C19A4A] bg-[#F8F1E2] text-[#6B4E1E]"
                }`}
              >
                {wipeResult.msg}
              </div>
            )}
          </div>
        </div>
      )}

      {masterOpen && (
        <MasterFileModal siloId={siloId} siloName={siloName} siloKind={siloKind}
          onClose={() => setMasterOpen(false)} />
      )}
      </>)}
    </div>
  );
}
